import { useCallback, useEffect, useRef, useState } from 'react'
import { mmss } from '../lib/format'
import { MAX_SECONDS, MIN_SECONDS } from '../lib/videoClip'

/**
 * The filmstrip trimmer — which part of a video becomes the clip.
 *
 * Until this existed, `makeClip()` took a start/end pair that nobody ever
 * passed, so every clip in the Collective was the first sixty seconds of
 * whatever was filmed. For a phone video that is the part where someone is
 * still walking back to the guitar. The hook is never at zero.
 *
 * Built the way the Photos trimmer is built, because that is the one video
 * editor every writer here has already used:
 *
 *   - The strip spans the WHOLE video, not the selection. The window is a
 *     bright box inside a dimmed strip, so you can always see how much you
 *     are leaving out — which is the thing you are actually deciding.
 *   - Dragging a handle scrubs the preview to that handle. You trim by
 *     watching the frame, not by reading a number.
 *   - Dragging the middle moves the window without resizing it, so "same
 *     length, ten seconds later" is one gesture rather than two.
 *
 * The clamps are the app's, not Apple's: never longer than MAX_SECONDS,
 * never shorter than MIN_SECONDS, and a video shorter than the minimum
 * never gets here — CollectiveClip refuses it at the picker.
 */

/** How tall the strip is, in CSS pixels. Thumbnails are drawn to match. */
const STRIP_H = 52

/**
 * Decode a row of frames out of the file.
 *
 * Seeking is serial on purpose. Asking a <video> for a new currentTime before
 * the last seek resolved drops the earlier one, and the strip comes back with
 * the same frame repeated — which reads as a broken video rather than a
 * still shot. Slower and correct beats instant and wrong here; the strip is
 * roughly a second's work for a phone clip.
 */
async function drawStrip(url, canvas, width, signal) {
  const v = document.createElement('video')
  v.src = url
  v.muted = true
  v.playsInline = true
  await new Promise((res, rej) => {
    v.onloadeddata = res
    v.onerror = () => rej(new Error('unreadable'))
  })
  if (signal.cancelled) return

  const ratio = (v.videoWidth || 9) / (v.videoHeight || 16)
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const thumbW = Math.max(18, STRIP_H * ratio)
  const n = Math.max(3, Math.ceil(width / thumbW))

  canvas.width = Math.round(width * dpr)
  canvas.height = Math.round(STRIP_H * dpr)
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)

  const dur = Number.isFinite(v.duration) ? v.duration : 0
  for (let i = 0; i < n; i++) {
    if (signal.cancelled) return
    // Sampled from the middle of each slot's slice of the video, so the first
    // thumbnail isn't the black frame most recordings open on.
    const t = dur * ((i + 0.5) / n)
    await new Promise((res) => {
      v.onseeked = res
      v.onerror = res
      v.currentTime = Math.min(t, Math.max(0, dur - 0.05))
      setTimeout(res, 2500)
    })
    if (signal.cancelled) return
    const w = width / n
    try {
      // Cover, not fit: a strip of letterboxed thumbnails is mostly black
      // bars, and the point of the strip is telling one moment from another.
      const sw = Math.min(v.videoWidth, v.videoHeight * (w / STRIP_H))
      ctx.drawImage(v, (v.videoWidth - sw) / 2, 0, sw, v.videoHeight,
                    i * w, 0, w + 0.5, STRIP_H)
    } catch { /* a frame that won't draw leaves its slot empty */ }
  }
  v.src = ''
}

export default function TrimStrip({ url, seconds, value, onChange,
                                    min = MIN_SECONDS, max = MAX_SECONDS }) {
  const wrap = useRef(null)
  const canvas = useRef(null)
  const video = useRef(null)
  const drag = useRef(null)
  const [w, setW] = useState(0)
  const [head, setHead] = useState(0)

  const dur = Math.max(0.1, seconds || 0)
  const { start, end } = value

  // Measure first, then draw: the strip is a bitmap, so it has to know its own
  // width in pixels before it can be the right one.
  useEffect(() => {
    const n = wrap.current
    if (!n) return
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width))
    ro.observe(n)
    setW(n.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (!url || !canvas.current || !w) return
    const signal = { cancelled: false }
    drawStrip(url, canvas.current, w, signal).catch(() => {})
    return () => { signal.cancelled = true }
  }, [url, w])

  const pct = (t) => `${(t / dur) * 100}%`

  /** Where on the strip a pointer is, in seconds. */
  const timeAt = (clientX) => {
    const r = wrap.current.getBoundingClientRect()
    return Math.max(0, Math.min(dur, ((clientX - r.left) / r.width) * dur))
  }

  const scrub = (t) => {
    const v = video.current
    if (v) { v.pause(); v.currentTime = Math.min(t, dur - 0.05) }
    setHead(t)
  }

  const onDown = (part) => (e) => {
    e.preventDefault()
    // Capture so a fast drag that leaves the strip keeps steering the handle
    // rather than dropping it wherever the finger crossed the edge. Guarded:
    // a pointer that's already been released throws here, and losing capture
    // is a worse drag, not a broken one.
    try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch { /* uncaptured */ }
    drag.current = { part, at: timeAt(e.clientX), start, end }
  }

  const onMove = (e) => {
    const d = drag.current
    if (!d) return
    const delta = timeAt(e.clientX) - d.at

    if (d.part === 'window') {
      // Moves, never resizes — the length you already chose is a decision,
      // and having it shrink because you nudged the box would undo it.
      const span = d.end - d.start
      const s = Math.max(0, Math.min(dur - span, d.start + delta))
      onChange({ start: s, end: s + span })
      scrub(s)
      return
    }
    if (d.part === 'start') {
      const s = Math.max(0, Math.min(d.end - min, d.start + delta, dur - min))
      onChange({ start: Math.max(s, d.end - max), end: d.end })
      scrub(Math.max(s, d.end - max))
      return
    }
    const e2 = Math.min(dur, Math.max(d.start + min, d.end + delta))
    onChange({ start: d.start, end: Math.min(e2, d.start + max) })
    scrub(Math.min(e2, d.start + max) - 0.15)
  }

  const onUp = () => { drag.current = null }

  /** Play the window and only the window, looping, like the feed will. */
  const onTime = useCallback((e) => {
    const v = e.target
    if (v.currentTime >= end || v.currentTime < start - 0.25) v.currentTime = start
    setHead(v.currentTime)
  }, [start, end])

  const span = end - start

  return (
    <div className="trim">
      <video ref={video} className="trim-preview" src={url} playsInline controls
             onTimeUpdate={onTime}
             onPlay={(e) => { if (e.target.currentTime < start || e.target.currentTime >= end) e.target.currentTime = start }} />

      <div className="trim-strip" ref={wrap}
           onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        <canvas ref={canvas} style={{ width: '100%', height: STRIP_H }} />

        {/* Everything outside the window, dimmed. Two panes rather than one
            box with a shadow: the cut-out has to be genuinely transparent or
            the frames you're choosing between are the ones you can't see. */}
        <div className="trim-out" style={{ left: 0, width: pct(start) }} />
        <div className="trim-out" style={{ left: pct(end), right: 0 }} />

        <div className="trim-win" style={{ left: pct(start), width: pct(span) }}
             onPointerDown={onDown('window')} />
        <div className="trim-h trim-h-a" style={{ left: pct(start) }}
             onPointerDown={onDown('start')} role="slider" tabIndex={0}
             aria-label="Clip starts" aria-valuemin={0} aria-valuemax={dur}
             aria-valuenow={start} aria-valuetext={mmss(start)}
             onKeyDown={(ev) => {
               const step = ev.shiftKey ? 1 : 0.2
               if (ev.key === 'ArrowLeft') onChange({ start: Math.max(0, Math.max(start - step, end - max)), end })
               if (ev.key === 'ArrowRight') onChange({ start: Math.min(end - min, start + step), end })
             }}>
          <i />
        </div>
        <div className="trim-h trim-h-b" style={{ left: pct(end) }}
             onPointerDown={onDown('end')} role="slider" tabIndex={0}
             aria-label="Clip ends" aria-valuemin={0} aria-valuemax={dur}
             aria-valuenow={end} aria-valuetext={mmss(end)}
             onKeyDown={(ev) => {
               const step = ev.shiftKey ? 1 : 0.2
               if (ev.key === 'ArrowLeft') onChange({ start, end: Math.max(start + min, end - step) })
               if (ev.key === 'ArrowRight') onChange({ start, end: Math.min(dur, Math.min(end + step, start + max)) })
             }}>
          <i />
        </div>

        <div className="trim-head" style={{ left: pct(head) }} aria-hidden="true" />
      </div>

      <p className="small trim-read">
        <b>{mmss(span)}</b> from {mmss(start)}
        {span >= max - 0.05 && <> · that's the longest a clip can be</>}
        {span <= min + 0.05 && span < max - 0.05 && <> · that's the shortest a clip can be</>}
      </p>
    </div>
  )
}
